import { FormRenderProps } from '@progress/kendo-react-form';
import { isDateComplete, isValueEmpty, shouldEndDueToFileDependency } from './FieldInteractionsHelper';
import { createFileFieldChangeHandler } from './createFileFieldChangeHandler';
import { createSearchableFieldHandlers } from './createSearchableFieldHandlers';
import type { FieldChangeHandler, FieldChangeHandlers } from './fieldInteractionsTypes';
import type { UseFieldInteractionApiReturn } from './useFieldInteractionApi';
import { ContentTypeFieldResponse, LoadFieldValuesFromFileRequest } from '@cs-cms/types';
import type { useFieldState } from './useFieldState';

type FieldState = ReturnType<typeof useFieldState>;

interface UseFieldChangeHandlersParams {
  ctFields: ContentTypeFieldResponse[];
  selectedCtName?: string;
  fieldState: FieldState;
  api: UseFieldInteractionApiReturn;
  contentTypeUsesFilesProcessor?: boolean;
  loadFieldValuesFromFile: (request: LoadFieldValuesFromFileRequest) => Promise<
    | {
        valuesForOtherFields?: Record<string, unknown>;
      }
    | null
    | undefined
  >;
}

export interface UseFieldChangeHandlersReturn {
  fieldHandlers: FieldChangeHandlers;
  onChoiceFilterChange: FieldChangeHandler;
  onManualSearch: FieldChangeHandler;
  handleFileFieldChange: (fieldName: string, files: FileList, formRenderProps: FormRenderProps) => Promise<void>;
  getManualSearchHandler: (field: ContentTypeFieldResponse) => FieldChangeHandler;
  getRefreshHandler: (field: ContentTypeFieldResponse) => FieldChangeHandler;
  getChoiceFilterHandler: (field: ContentTypeFieldResponse) => FieldChangeHandler;
}

const noopFieldChangeHandler: FieldChangeHandler = async () => {};

export const useFieldChangeHandlers = ({
  ctFields,
  selectedCtName,
  fieldState,
  api,
  contentTypeUsesFilesProcessor,
  loadFieldValuesFromFile
}: UseFieldChangeHandlersParams): UseFieldChangeHandlersReturn => {
  const { callLoadFieldValuesApi, applyLookupResponseToForm, handleDependencies, runLookupForField } = api;

  const { onStringNumberFieldChange, onChoiceFilterChange } = createSearchableFieldHandlers({
    ctFields,
    fieldState,
    callLoadFieldValuesApi,
    applyLookupResponseToForm,
    handleDependencies,
    runLookupForField
  });

  const onUndefinedFieldChange: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    formRenderProps.onChange(fieldName, { value });
  };

  const onDateFieldChange: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field || shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    if (isDateComplete(value) && field.formFiller && field.usesExtDictionary) {
      await runLookupForField(fieldName, value, formRenderProps);
    }
  };

  const onBoolFieldChange: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field || shouldEndDueToFileDependency(field)) {
      return;
    }

    if (field.formFiller && field.usesExtDictionary) {
      await runLookupForField(fieldName, value, formRenderProps);
    }
  };

  const onChoiceSelectionChange: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field || shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    if (field.formFiller && field.usesExtDictionary && !isValueEmpty(value)) {
      await runLookupForField(fieldName, value, formRenderProps);
    }
  };

  const handleFileFieldChange = createFileFieldChangeHandler({
    ctFields,
    selectedCtName,
    fieldState,
    applyLookupResponseToForm,
    contentTypeUsesFilesProcessor,
    loadFieldValuesFromFile
  });

  const onManualSearch: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    await api.runLookupForField(fieldName, value, formRenderProps);
  };

  const getManualSearchHandler = (field: ContentTypeFieldResponse): FieldChangeHandler => {
    if (field.searchable && field.formFiller && field.usesExtDictionary) {
      return onManualSearch;
    }
    return noopFieldChangeHandler;
  };

  const getRefreshHandler = (field: ContentTypeFieldResponse): FieldChangeHandler => {
    if (field.formFiller && field.usesExtDictionary) {
      return onManualSearch;
    }
    return noopFieldChangeHandler;
  };

  const getChoiceFilterHandler = (field: ContentTypeFieldResponse): FieldChangeHandler => {
    if (field.searchable && field.usesExtDictionary) {
      return onChoiceFilterChange;
    }
    return noopFieldChangeHandler;
  };

  return {
    fieldHandlers: {
      onUndefinedFieldChange,
      onStringNumberFieldChange,
      onDateFieldChange,
      onBoolFieldChange,
      onChoiceSelectionChange
    },
    onChoiceFilterChange,
    onManualSearch,
    handleFileFieldChange,
    getManualSearchHandler,
    getRefreshHandler,
    getChoiceFilterHandler
  };
};
